import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { CornerDownRight, Info, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input, inputClassName } from '@/components/ui/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import TelephoneNumberInput from '../../Form/TelephoneNumberInput'
import { EventTypeLocation, EventTypeLocationType } from '../../../types'
import { locationLabel } from '../Booker/format'
import { CONFERENCING_LOCATIONS, isOrganizerLocation, isPhoneLocation, LocationIcon } from '../locations'

interface IProps {
  locations: EventTypeLocation[]
  // Calendar types the host connected ('google' / 'outlook'); conferencing locations need one
  connectedCalendarTypes: string[]
  onChange: (locations: EventTypeLocation[]) => void
}

const LOCATION_CALENDAR: Partial<Record<EventTypeLocationType, string>> = {
  [EventTypeLocationType.GOOGLE_MEET]: 'google',
  [EventTypeLocationType.MICROSOFT_TEAMS]: 'outlook',
}

const newLocation = (type: EventTypeLocationType): EventTypeLocation => ({ type, value: null, display_on_booking_page: false })

export default function LocationsEditor({ locations, connectedCalendarTypes, onChange }: IProps) {
  const { t } = useTranslation()
  const [adding, setAdding] = React.useState(false)
  const uid = React.useId()

  const groups: Array<{ label: string, types: EventTypeLocationType[] }> = [
    { label: t('Scheduling::Conferencing'), types: CONFERENCING_LOCATIONS },
    { label: t('Scheduling::In person'), types: [EventTypeLocationType.IN_PERSON_ORGANIZER, EventTypeLocationType.IN_PERSON_ATTENDEE] },
    { label: t('Scheduling::Phone'), types: [EventTypeLocationType.PHONE_ORGANIZER, EventTypeLocationType.PHONE_ATTENDEE] },
  ]

  const isAvailable = (type: EventTypeLocationType) => {
    const calendar = LOCATION_CALENDAR[type]
    return !calendar || connectedCalendarTypes.includes(calendar)
  }

  // A row may keep its own type even when its calendar got disconnected
  const optionsFor = (current: EventTypeLocationType | null) => groups
    .map(group => ({
      ...group,
      types: group.types.filter(type => type === current || (isAvailable(type) && !locations.some(location => location.type === type))),
    }))
    .filter(group => group.types.length > 0)

  const items = Object.values(EventTypeLocationType).map(type => ({ value: type, label: locationLabel(t, type) }))
  const canAdd = optionsFor(null).length > 0

  const update = (index: number, patch: Partial<EventTypeLocation>) => {
    onChange(locations.map((location, i) => i === index ? { ...location, ...patch } : location))
  }

  const renderSelect = (value: EventTypeLocationType | null, onValueChange: (type: EventTypeLocationType) => void) => (
    <Select items={items} value={value} onValueChange={(type: EventTypeLocationType | null) => type && onValueChange(type)}>
      <SelectTrigger className='h-9 w-full'>
        <SelectValue placeholder={t('Scheduling::Select...')}>
          {(type: EventTypeLocationType | null) => type
            ? <><LocationIcon type={type} className='size-4' />{locationLabel(t, type)}</>
            : t('Scheduling::Select...')}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {optionsFor(value).map(group => (
          <SelectGroup key={group.label}>
            <SelectLabel>{group.label}</SelectLabel>
            {group.types.map(type => (
              <SelectItem key={type} value={type}>
                <LocationIcon type={type} className='size-4' />
                {locationLabel(t, type)}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  )

  return (
    <TooltipProvider>
      <div className='flex flex-col gap-3'>
        {locations.map((location, index) => (
          <div key={location.type} className='flex flex-col gap-2'>
            <div className='flex items-center gap-2'>
              {renderSelect(location.type, type => update(index, newLocation(type)))}
              <Button type='button' variant='ghost' size='icon' aria-label={t('Scheduling::Remove location')}
                onClick={() => onChange(locations.filter((_, i) => i !== index))}>
                <X />
              </Button>
            </div>

            {isOrganizerLocation(location.type) && (
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <CornerDownRight className='size-4 shrink-0 text-muted-foreground' />
                  {isPhoneLocation(location.type) ? (
                    <TelephoneNumberInput
                      initialValue={location.value || ''}
                      onChangeNumber={number => update(index, { value: number })}
                      inputProps={{ 'aria-label': t('Scheduling::Phone number'), className: inputClassName }}
                    />
                  ) : (
                    <Input
                      aria-label={t('Scheduling::Address')}
                      placeholder={t('Scheduling::Enter address')}
                      maxLength={500}
                      value={location.value || ''}
                      onChange={e => update(index, { value: e.target.value })}
                    />
                  )}
                </div>
                <Field orientation='horizontal' className='pl-6'>
                  <Checkbox
                    id={`${uid}-${location.type}-display`}
                    checked={location.display_on_booking_page}
                    onCheckedChange={(checked: boolean) => update(index, { display_on_booking_page: checked })}
                  />
                  <FieldLabel htmlFor={`${uid}-${location.type}-display`} className='font-normal'>
                    {t('Scheduling::Display on booking page')}
                  </FieldLabel>
                  <Tooltip>
                    <TooltipTrigger render={<span className='inline-flex text-muted-foreground' />}>
                      <Info className='size-4' />
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('Scheduling::When unchecked, the booker only sees this after booking: on the confirmation page, in the email and in the calendar invite.')}
                    </TooltipContent>
                  </Tooltip>
                </Field>
              </div>
            )}
          </div>
        ))}

        {adding && canAdd && renderSelect(null, type => {
          onChange([...locations, newLocation(type)])
          setAdding(false)
        })}

        {canAdd && !adding && (
          <Button type='button' variant='ghost' className='self-start' onClick={() => setAdding(true)}>
            <Plus /> {t('Scheduling::Add a location')}
          </Button>
        )}
      </div>
    </TooltipProvider>
  )
}
